import type { Point } from "@labeling-tools/ui-components/src/ImageWithBoundingBox";
import { toSvgPolygonPoints } from "@labeling-tools/ui-components/src/utils/obbGeometry";
import { boxOutlineWidth } from "@/utils/coverage-beta-box-outline";
import { cn } from "@/utils/utils";
import type { CSSProperties } from "react";

interface Props {
  /** Detection corners in the wrapper div's local 0-100 space. */
  corners: Point[];
  /**
   * Tailwind `stroke-*` / `fill-*` utilities for the quad. `group-hover:*`
   * variants work as long as the wrapper div carries `group`.
   */
  className?: string;
  /** The outline's weight at 1x, in px — and its ceiling as the view zooms. */
  strokeWidth?: number;
  dashed?: boolean;
  /** A wider stroke underneath — the quad stand-in for a Tailwind `ring`. */
  ringClassName?: string;
}

/** Holds the resolved outline width, so the ring and dashes scale with it. */
const OUTLINE_VAR = "--cb-box-outline";

/**
 * Draws a detection as an SVG polygon filling its wrapper div. The div stays
 * positioned on the detection's AABB and keeps owning the pointer events; this
 * overlay only carries the shape, so the two never fight over hit testing.
 *
 * Every box draws through here, axis-aligned ones included, because a CSS
 * border can't stay thin as the view zooms in — see `coverage-beta-box-outline`
 * for the widths. Strokes are non-scaling so the anisotropic
 * `preserveAspectRatio="none"` viewBox can't skew their thickness per axis.
 */
export const OrientedBoxOverlay = (props: Props) => {
  const {
    corners,
    className,
    strokeWidth = 1,
    dashed = false,
    ringClassName,
  } = props;
  const points = toSvgPolygonPoints(corners, 1);
  const outline = `var(${OUTLINE_VAR})`;

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 block size-full overflow-visible pointer-events-none"
      style={
        {
          // Size container for the outline's `cqmin` term: this element is
          // stretched over the box, so 1cqmin is 1% of the box's short side.
          containerType: "size",
          [OUTLINE_VAR]: boxOutlineWidth(strokeWidth),
        } as CSSProperties
      }
    >
      {ringClassName && (
        <polygon
          points={points}
          className={cn("fill-none", ringClassName)}
          // 5x the outline, standing in for a `ring-2` over a 1px line.
          style={{ strokeWidth: `calc(${outline} * 5)` }}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      )}
      <polygon
        points={points}
        className={cn("fill-none", className)}
        style={{
          strokeWidth: outline,
          // Dash and gap keep their proportions to the outline (was `4 3`).
          strokeDasharray: dashed
            ? `calc(${outline} * 2) calc(${outline} * 1.5)`
            : undefined,
        }}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
    </svg>
  );
};
